import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { useSearchParams } from 'react-router-dom';
import { CashflowHistoryData, fetchCashflowHistory } from '../../helpers/plxOrdersData';
import Explainer, { ExplainerSection } from '../../components/Explainer';
import InfoTip from '../../components/InfoTip';
import { tokens } from '../../theme';
import { useChartTheme } from '../../themes/chartTheme';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatNumber } from '../../helpers/numberUtils';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';

const COLOR_QA = '#4caf50';
const COLOR_UPSELL = '#2196f3';
const COLOR_CHARGEBACK = '#e53935';
const COLOR_PLAN_GROSS = '#9e9e9e';
const COLOR_PLAN_ADJ = '#ff9800';

const explainerSections: ExplainerSection[] = [
  {
    label: 'Datengrundlage',
    body: 'Ist = das real eingegangene Geld je Kalenderwoche, ermittelt aus einem täglichen Schnappschuss der tatsächlichen Zahlungseingänge je Bestellung. Plan = der Forecast, wie er zu Quartalsbeginn eingefroren wurde („Budget"). Nur Quartalsabo + Upsell.',
  },
  {
    label: 'Berechnung',
    body: 'Ist netto = alle echten Zahlungseingänge der Woche minus Rückbuchungen. Plan = die zu Quartalsbeginn erwartete Summe derselben Woche. Drift = wie viel Prozent das aufsummierte Ist über oder unter dem aufsummierten Plan liegt.',
  },
  {
    label: 'Zwei Plan-Linien: bereinigt vs. brutto',
    body: 'Der Plan hat bewusst zwei Linien, die ein Band aufspannen. Plan brutto (grau) = die volle erwartete Rechnungssumme, als ob jede bekannte Bestellung zahlt — das optimistische Szenario. Plan bereinigt (orange) = dieselben Bestellungen, aber Testphasen (ANFO) nur mit ihrer erfahrungsgemäßen Conversion-Rate (~15–30%) gezählt — ein vorsichtiger, risikogewichteter Boden, KEIN Ziel. Beide Linien kennen nur die Pipeline, die zu Quartalsbeginn eingefroren wurde.',
  },
  {
    label: 'Warum den kumulativen Verlauf ansehen (nicht einzelne Wochen)',
    body: 'Der Plan pinnt jede Rechnung auf genau eine Woche, die Realität schmiert sie über Nachbarwochen (Lastschrift-/Mahnzyklen laufen ±1–2 Wochen versetzt). Eine Einzelwoche kann deshalb weit unter ODER über dem Plan liegen — Geld, das der Plan für KW22 erwartet, taucht in KW24 auf. Im KUMULATIVEN Verlauf (oberer Chart) heben sich diese Verschiebungen auf: dort liegt die Ist-Linie sinnvoll im Band, und der Abstand zum Band ist die echte Drift. Der untere Balken-Chart zeigt nur die Zusammensetzung der Eingänge, bewusst ohne Plan-Vergleich.',
  },
  {
    label: 'Aussage',
    body: 'Liegen wir übers Quartal gesehen über oder unter Budget? Die Antwort ist der kumulative Verlauf oben bzw. die Kachel „Drift Plan vs Ist" — grün = mehr Geld als geplant, rot = weniger. Gemessen wird gegen den vorsichtigen (bereinigten) Plan, ist also im Zweifel eher positiv.',
  },
  {
    label: 'Bitte unbedingt beachten',
    highlight: true,
    body: 'Aussagekräftig ist nur die KUMULATIVE Sicht (obere Kurve + Drift-Kachel), nicht der einzelne Wochenbalken — der ist Timing-Rauschen. Frühe Quartalswochen wirken oft „unter Plan" und holen später auf. Das echte Ist kann kumuliert sogar über dem Brutto-Plan liegen, weil Bestellungen, die erst NACH dem Freeze starteten, noch im Quartal abrechnen — der eingefrorene Plan kennt sie nicht. Er kann daher auch von der „live" nachgeführten Zahl im Forecast-Tab abweichen. Es werden nur Wochen mit sauberen Daten gezeigt: Quartale, in denen die Snapshots erst mittendrin starteten (Teil-Quartal), beginnen entsprechend später.',
  },
];

const CashflowHistoryTab: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const [data, setData] = useState<CashflowHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  // Ladefehler sichtbar machen statt endlosem Spinner (Bedienprüfung 28.10.).
  const [fehler, setFehler] = useState<string | null>(null);
  // Quarter lives in the URL (?quarter=2026-Q2) so a Rückblick view is linkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const [quarter, setQuarter] = useState<string>(() => searchParams.get('quarter') || '');

  const handleQuarterChange = (value: string) => {
    setQuarter(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'history'); // keep the tab pinned when sharing a quarter link
        next.set('quarter', value);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFehler(null);
      try {
        const result = await fetchCashflowHistory(quarter || undefined);
        if (!cancelled) setData(result);
      } catch (error) {
        console.error('Error loading cashflow history:', error);
        // A malformed quarter param (e.g. hand-mangled ?quarter=2099-Q9) 500s the
        // endpoint — fall back to the default once instead of a stuck spinner.
        // Clearing quarter refetches the default; the sync effect then heals the
        // URL. (setSearchParams is intentionally NOT touched here / not a dep — it
        // is not referentially stable and would retrigger this fetch effect.)
        if (!cancelled && quarter) {
          setQuarter('');
        } else if (!cancelled) {
          setFehler(error instanceof Error ? error.message : String(error));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [quarter]);

  // Keep the URL in sync with the shown quarter, and self-heal a stale/bogus
  // quarter param (e.g. an old shared link whose quarter has since aged out of
  // quarters_available — the endpoint echoes it back with zero weeks).
  useEffect(() => {
    if (!data) return;
    const isValid = data.quarters_available.some((q) => q.value === data.quarter);
    if (!isValid) {
      setQuarter(''); // drop it → refetch resolves the default quarter
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('quarter');
          return next;
        },
        { replace: true },
      );
      return;
    }
    if (searchParams.get('quarter') !== data.quarter) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'history');
          next.set('quarter', data.quarter);
          return next;
        },
        { replace: true },
      );
    }
  }, [data, searchParams, setSearchParams]);

  // Chart data: Ist stacked (positive) + chargebacks (negative); plan carried as
  // extra fields for the custom line layer.
  const chartData = useMemo(() => {
    if (!data) return [];
    return data.weeks.map((w) => ({
      week: w.week_label,
      Quartalsabo: w.quartalsabo_gross,
      Upsell: w.upsell_gross,
      Rückbuchungen: w.chargebacks,
      planGross: w.plan_gross,
      planAdjusted: w.plan_adjusted,
      istNet: w.ist_net,
      isFuture: w.is_future,
      isPartial: w.is_partial,
      hasGap: w.has_gap,
    }));
  }, [data]);

  // Bar chart is composition-only now (no plan lines) — scale to the Ist stack.
  const { minValue, maxValue } = useMemo(() => {
    if (!chartData.length) return { minValue: 0, maxValue: 100 };
    let max = 0;
    let min = 0;
    for (const d of chartData) {
      max = Math.max(max, d.Quartalsabo + d.Upsell);
      min = Math.min(min, d['Rückbuchungen']);
    }
    return { minValue: Math.floor(min * 1.15), maxValue: Math.ceil(max * 1.1) };
  }, [chartData]);

  // Cumulative (burn-up) series: running totals over the SAME weeks the service
  // sums into the cumulative KPIs — !is_future && !is_partial && !no_data
  // (CashflowHistoryService.ts). This cancels the per-week timing noise (money
  // shifting between adjacent weeks nets out) and makes the final point of each
  // line equal the corresponding KPI (cum_ist_net / cum_plan_adjusted / _gross).
  const cumulativeSeries = useMemo(() => {
    if (!data) return { series: [], hasData: false };
    let ci = 0;
    let ca = 0;
    let cg = 0;
    const ist: { x: string; y: number }[] = [];
    const adj: { x: string; y: number }[] = [];
    const gross: { x: string; y: number }[] = [];
    for (const w of data.weeks) {
      if (w.is_future || w.is_partial || w.no_data) continue;
      ci += w.ist_net;
      ca += w.plan_adjusted;
      cg += w.plan_gross;
      ist.push({ x: w.week_label, y: Math.round(ci) });
      adj.push({ x: w.week_label, y: Math.round(ca) });
      gross.push({ x: w.week_label, y: Math.round(cg) });
    }
    return {
      hasData: ist.length > 0,
      series: [
        { id: 'Plan brutto (kum.)', data: gross },
        { id: 'Plan bereinigt (kum.)', data: adj },
        { id: 'Ist (kum.)', data: ist },
      ],
    };
  }, [data]);

  if (loading || (!data && !fehler)) {
    return <LoadingSpinner />;
  }
  if (!data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Der Cashflow-Rückblick konnte nicht geladen werden ({fehler}). Bitte die Seite neu laden.
      </Alert>
    );
  }

  // Only quarters with a clean quarter-start baseline are shown. Until the first
  // one exists (snapshot pipeline must have been running at a quarter start),
  // there is nothing 100%-reliable to compare.
  if (!data.quarters_available.length) {
    return (
      <Box>
        <Explainer
          title="Was zeigt Plan vs Ist — und wie werden die Zahlen berechnet?"
          sections={explainerSections}
        />
        <Alert severity="info">
          Noch keine vollständige Quartals-Baseline verfügbar. Ein Quartal wird erst ausgewertet,
          wenn die täglichen Snapshots bereits zum Quartalsstart liefen — die erste saubere
          Auswertung erscheint automatisch, sobald ein solches Quartal vorliegt.
        </Alert>
      </Box>
    );
  }

  const { summary } = data;
  const driftPositive = (summary.drift_pct ?? 0) >= 0;

  // Custom Nivo line layer: shade the band between the two cumulative plan lines
  // so "Ist liegt im Band" reads at a glance. Uses pre-computed pixel positions
  // (p.position) so it stays aligned with the point scale.
  const CumBandLayer = ({ series }: any) => {
    const gross = series.find((s: any) => s.id === 'Plan brutto (kum.)');
    const adj = series.find((s: any) => s.id === 'Plan bereinigt (kum.)');
    if (!gross || !adj || !gross.data.length) return null;
    const top = gross.data.map((p: any) => `${p.position.x},${p.position.y}`);
    const bottom = [...adj.data].reverse().map((p: any) => `${p.position.x},${p.position.y}`);
    return (
      <polygon
        points={[...top, ...bottom].join(' ')}
        fill={COLOR_PLAN_ADJ}
        fillOpacity={0.1}
        stroke="none"
      />
    );
  };

  const kpiCards = [
    {
      title: 'Kum. Ist (netto)',
      value: `${formatNumber(summary.cum_ist_net, 0)} €`,
      subtitle: `${summary.completed_weeks} abgeschlossene KW`,
      color: colors.greenAccent[600],
      tooltip:
        'Das real eingegangene Geld (Quartalsabo + Upsell) minus Rückbuchungen, aufsummiert über die abgeschlossenen Wochen des Quartals. Das ist tatsächlicher Zahlungseingang, keine Schätzung.',
    },
    {
      title: 'Kum. Plan (bereinigt)',
      value: `${formatNumber(summary.cum_plan_adjusted, 0)} €`,
      subtitle: `Brutto: ${formatNumber(summary.cum_plan_gross, 0)} €`,
      color: colors.blueAccent[600],
      tooltip:
        'Was wir für dieselben Wochen erwartet hatten — der Forecast, eingefroren zu Quartalsbeginn (das „Budget"). Bereinigt = Testphasen nur mit ihrer Conversion-Rate gezählt, also ein vorsichtiger Boden, kein Ziel. Das Ist liegt normal zwischen bereinigt und brutto. Ändert sich innerhalb des Quartals nicht mehr.',
    },
    {
      title: 'Drift Plan vs Ist',
      value:
        summary.drift_pct === null
          ? '–'
          : `${driftPositive ? '+' : ''}${formatNumber(summary.drift_pct, 1)} %`,
      subtitle: driftPositive ? 'über Plan' : 'unter Plan',
      color: driftPositive ? colors.greenAccent[600] : COLOR_CHARGEBACK,
      tooltip:
        'Die wichtigste Zahl: liegt das real eingegangene Geld übers Quartal gesehen über (+) oder unter (–) dem Budget? Nur diese kumulative Sicht ist verlässlich — einzelne Wochenbalken schwanken stark und sagen für sich genommen wenig aus.',
    },
    {
      title: 'Rückbuchungen',
      value: `${formatNumber(summary.cum_chargebacks, 0)} €`,
      subtitle: 'im Zeitraum',
      color: COLOR_CHARGEBACK,
      tooltip:
        'Zurückgebuchtes Geld (Rücklastschrift / Rücküberweisung) in den abgeschlossenen Wochen. Ist im „Ist netto" bereits abgezogen — hier zusätzlich einzeln sichtbar.',
    },
  ];

  // Detail table
  const headerMapping: Record<string, HeaderMappingItem> = {
    week_label: { name: 'KW', type: 'string' },
    quartalsabo_gross: { name: 'Quartalsabo', type: 'number', digits: 0, attach: ' €' },
    upsell_gross: { name: 'Upsell', type: 'number', digits: 0, attach: ' €' },
    chargebacks: { name: 'Rückbuchungen', type: 'number', digits: 0, attach: ' €' },
    ist_net: { name: 'Ist netto', type: 'number', digits: 0, attach: ' €' },
    plan_adjusted: { name: 'Plan (ber.)', type: 'number', digits: 0, attach: ' €' },
    plan_gross: { name: 'Plan (brutto)', type: 'number', digits: 0, attach: ' €' },
    status: { name: 'Status', type: 'string' },
  };

  const tableData = data.weeks.map((w) => {
    const status = w.is_future
      ? 'geplant'
      : w.no_data
        ? 'keine Daten'
        : w.is_partial
          ? 'laufend'
          : w.has_gap
            ? 'Lücke'
            : 'abgeschl.';
    return {
      week_label: w.week_label,
      quartalsabo_gross: w.quartalsabo_gross,
      upsell_gross: w.upsell_gross,
      chargebacks: w.chargebacks,
      ist_net: w.ist_net,
      plan_adjusted: w.plan_adjusted,
      plan_gross: w.plan_gross,
      status,
    };
  });

  const computeSummary = (rows: (typeof tableData)[number][]) => {
    const s = {
      week_label: 'Insgesamt',
      quartalsabo_gross: 0,
      upsell_gross: 0,
      chargebacks: 0,
      ist_net: 0,
      plan_adjusted: 0,
      plan_gross: 0,
      status: '',
    };
    rows.forEach((r) => {
      s.quartalsabo_gross += Number(r.quartalsabo_gross) || 0;
      s.upsell_gross += Number(r.upsell_gross) || 0;
      s.chargebacks += Number(r.chargebacks) || 0;
      s.ist_net += Number(r.ist_net) || 0;
      s.plan_adjusted += Number(r.plan_adjusted) || 0;
      s.plan_gross += Number(r.plan_gross) || 0;
    });
    return s;
  };

  return (
    <Box>
      <Explainer
        title="Was zeigt Plan vs Ist — und wie werden die Zahlen berechnet?"
        sections={explainerSections}
      />

      {/* Quarter selector */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Quartal</InputLabel>
          <Select
            value={
              data.quarters_available.some((q) => q.value === data.quarter) ? data.quarter : ''
            }
            label="Quartal"
            onChange={(e) => handleQuarterChange(e.target.value)}
          >
            {data.quarters_available.map((q) => (
              <MenuItem key={q.value} value={q.value}>
                {q.label}
                {!q.baseline_complete ? ' (Teil-Quartal)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {data.baseline_date && (
          <Typography variant="caption" color="text.secondary">
            Plan-Baseline eingefroren am {new Date(data.baseline_date).toLocaleDateString('de-DE')}
          </Typography>
        )}
      </Box>

      {!data.baseline_complete && data.weeks.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Teil-Quartal: Die täglichen Snapshots starteten erst am{' '}
          {data.snapshots_started
            ? new Date(data.snapshots_started).toLocaleDateString('de-DE')
            : 'später'}
          , mitten im Quartal. Die Auswertung beginnt daher erst ab {data.weeks[0].week_label} —
          frühere Wochen fehlen, sind aber aus Sauberkeitsgründen ausgeblendet.
        </Alert>
      )}

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Tooltip title={card.tooltip} arrow placement="top">
              <Card elevation={2} sx={{ height: '100%', cursor: 'help' }}>
                <CardContent>
                  <Typography variant="subtitle2" color={colors.grey[100]} gutterBottom>
                    {card.title}
                  </Typography>
                  <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold', mb: 0.5 }}>
                    {card.value}
                  </Typography>
                  <Typography variant="caption" color={colors.grey[300]}>
                    {card.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* Cumulative burn-up — the honest Plan-vs-Ist view (per-week timing cancels) */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Kumulativer Verlauf — Ist vs. Plan
            <InfoTip text="Aufsummiertes echtes Geld (grün) gegen das aufsummierte Plan-Band (orange = bereinigt/vorsichtig, grau = brutto/alle zahlen), Woche für Woche. Hier heben sich Timing-Verschiebungen zwischen Wochen auf — der Abstand der grünen Linie zum Band ist die verlässliche Drift. Liegt die grüne Linie im Band, sind wir auf Kurs; darüber = über Budget, darunter = dahinter. Nur abgeschlossene Wochen mit sauberen Daten." />
          </Typography>
          {cumulativeSeries.hasData ? (
            <Box height="420px">
              <ResponsiveLine
                data={cumulativeSeries.series as any}
                theme={chartTheme}
                margin={{ top: 20, right: 180, bottom: 55, left: 80 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
                curve="monotoneX"
                colors={({ id }: any) =>
                  id === 'Ist (kum.)'
                    ? COLOR_QA
                    : id === 'Plan bereinigt (kum.)'
                      ? COLOR_PLAN_ADJ
                      : COLOR_PLAN_GROSS
                }
                lineWidth={2.5}
                enablePoints
                pointSize={6}
                enableGridX={false}
                enableGridY
                axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: -30 }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  legend: 'EUR (kumuliert)',
                  legendPosition: 'middle',
                  legendOffset: -60,
                  format: (value: any) =>
                    Math.abs(Number(value)) >= 1000
                      ? `${(Number(value) / 1000).toFixed(0)}k`
                      : String(value),
                }}
                layers={[
                  'grid',
                  'markers',
                  'axes',
                  CumBandLayer,
                  'areas',
                  'lines',
                  'points',
                  'slices',
                  'mesh',
                  'legends',
                ]}
                enableSlices="x"
                sliceTooltip={({ slice }: any) => {
                  const byId: Record<string, number> = {};
                  slice.points.forEach((p: any) => {
                    byId[p.serieId] = Number(p.data.y);
                  });
                  const ist = byId['Ist (kum.)'];
                  const adj = byId['Plan bereinigt (kum.)'];
                  const gross = byId['Plan brutto (kum.)'];
                  const drift = adj ? (ist / adj - 1) * 100 : null;
                  const pos =
                    ist == null
                      ? ''
                      : ist > gross
                        ? 'über dem Brutto-Plan → über Budget'
                        : ist < adj
                          ? 'unter dem bereinigten Boden → dahinter'
                          : 'im Plan-Band → auf Kurs';
                  return (
                    <Box
                      sx={{
                        padding: '8px 12px',
                        color: '#222',
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        fontSize: 13,
                      }}
                    >
                      <strong>{slice.points[0]?.data?.x} — kumuliert</strong>
                      <hr
                        style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #eee' }}
                      />
                      Ist: <strong>{formatNumber(ist, 0)} €</strong>
                      <br />
                      Plan: {formatNumber(adj, 0)} € (ber.) – {formatNumber(gross, 0)} € (brutto)
                      {drift !== null && (
                        <>
                          <br />
                          <span style={{ color: '#666' }}>
                            Drift {drift >= 0 ? '+' : ''}
                            {formatNumber(drift, 1)} % — {pos}
                          </span>
                        </>
                      )}
                    </Box>
                  );
                }}
                legends={[
                  {
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 170,
                    translateY: 0,
                    itemsSpacing: 4,
                    itemWidth: 160,
                    itemHeight: 20,
                    itemDirection: 'left-to-right',
                    symbolSize: 12,
                    symbolShape: 'square',
                  },
                ]}
              />
            </Box>
          ) : (
            <Alert severity="info">
              Noch keine abgeschlossenen Wochen mit sauberen Daten in diesem Quartal.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Per-week composition (descriptive — NO plan comparison, that lives in the burn-up above) */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Zahlungseingänge je Woche (Zusammensetzung)
            <InfoTip text="Zeigt nur, WAS wann reinkam: Quartalsabo (grün) + Upsell (blau) als echte Eingänge, Rückbuchungen (rot, unter Null). Bewusst OHNE Plan-Linien — der Plan-Vergleich je Woche wäre irreführend, weil Zahlungen zwischen Wochen wandern (Lastschrift-/Mahnzyklen). Ob wir über/unter Budget liegen, steht im kumulativen Verlauf oben." />
          </Typography>
          <Box height="440px">
            <ResponsiveBar
              data={chartData as any}
              keys={['Quartalsabo', 'Upsell', 'Rückbuchungen']}
              indexBy="week"
              theme={chartTheme}
              margin={{ top: 20, right: 170, bottom: 55, left: 80 }}
              padding={0.35}
              minValue={minValue}
              maxValue={maxValue}
              valueScale={{ type: 'linear' }}
              indexScale={{ type: 'band', round: true }}
              colors={({ id }) =>
                id === 'Quartalsabo' ? COLOR_QA : id === 'Upsell' ? COLOR_UPSELL : COLOR_CHARGEBACK
              }
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: -30 }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                legend: 'EUR',
                legendPosition: 'middle',
                legendOffset: -60,
                format: (value) =>
                  Math.abs(Number(value)) >= 1000
                    ? `${(Number(value) / 1000).toFixed(0)}k`
                    : String(value),
              }}
              enableGridY
              labelSkipWidth={16}
              labelSkipHeight={16}
              labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
              layers={['grid', 'axes', 'bars', 'markers', 'legends']}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 160,
                  translateY: 0,
                  itemsSpacing: 2,
                  itemWidth: 150,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 12,
                  symbolShape: 'square',
                },
              ]}
              tooltip={({ id, value, indexValue }) => {
                const row = chartData.find((d) => d.week === indexValue);
                return (
                  <Box
                    sx={{
                      padding: '8px 12px',
                      color: '#222',
                      background: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      fontSize: 13,
                    }}
                  >
                    <strong>
                      {indexValue}
                      {row?.isPartial ? ' (laufend)' : ''}
                      {row?.isFuture ? ' (geplant)' : ''}
                    </strong>
                    <br />
                    {id}: {formatNumber(Number(value), 2)} €
                    {row && (
                      <>
                        <hr
                          style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #eee' }}
                        />
                        Ist netto: <strong>{formatNumber(row.istNet, 0)} €</strong>
                        {!row.isFuture && (
                          <>
                            <br />
                            <span style={{ color: '#666' }}>
                              Einzelwoche = Timing-Rauschen (Zahlungen wandern zwischen Wochen).
                              Plan-Vergleich siehe kumulativer Verlauf oben.
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </Box>
                );
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Detail table */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Wochen-Detail
            <InfoTip text="Rohzahlen je Kalenderwoche. Status: abgeschl. = Woche vorbei mit vollständigen Daten, laufend = aktuelle Woche, Lücke = ein Snapshot-Tag fehlte (Wochensumme trotzdem korrekt), keine Daten = kein Snapshot (fließt nicht in die Drift ein), geplant = Zukunft. Bewusst ohne Wochen-Drift — der verlässliche Plan-Vergleich ist der kumulative Verlauf oben." />
          </Typography>
          <Box
            sx={{ backgroundColor: colors.primary[400], borderRadius: '4px', overflow: 'hidden' }}
          >
            <TableWidget
              key={`history-${data.quarter}`}
              getData={async () => tableData}
              computeSummary={computeSummary}
              detailsHeaderMapping={headerMapping}
              initialSortColumn="week_label"
              sortOrder="asc"
              excelDownloadName={`Cashflow_Rueckblick_${data.quarter}`}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CashflowHistoryTab;
